/* @flow */
import produce from 'immer';
import { message } from 'antd';
import { actionTypes } from './actions';
import { actionTypes as leadsActionsTypes } from '../leads/actions';
import { defineServerErrors } from '@/utils/redux';

const {
  FETCH_LEAD,
  FETCH_SUCCESS,
  FETCH_FAILED,

  UPDATE_LEAD,
  UPDATE_SUCCESS,
  UPDATE_FAILED,

  DELETE_LEAD,
  DELETE_SUCCESS,
  DELETE_FAILED,

  CHANGE_TAB,

  SET_EDIT_MODE,
  SAVE_EDIT_DATA,
  CLEAR_EDIT_DATA,
  REMOVE_SYSTEM_IMAGE,

  FETCH_PROJECT_INFORMATION_DETAILS,
  FETCH_PROJECT_INFORMATION_DETAILS_SUCCESS,
  FETCH_PROJECT_INFORMATION_DETAILS_FAILED,

  FETCH_DOCUMENT,
  FETCH_DOCUMENT_SUCCESS,
  FETCH_DOCUMENT_FAILED,

  RESET_DOCUMENT_URL,
  RESET_DOCUMENT_URL_SUCCESS,
  RESET_DOCUMENT_URL_FAILED,
} = actionTypes;

type State = {
  isFetching: boolean,
  data: LeadT,
  fetchErrors: ?Array<string>,
  error: ?Object,
  activeTab: string,
  isEditing: boolean,
  editFormData: Object,
  editErrors: ?Array<string>,
  deleteSucceed: ?boolean,
  deleteErrors: ?Array<string>,
  documentUrl: string,
};

const initialState = {
  isFetching: false,
  isEditing: false,
  editFormData: {},
  data: {
    id: 0,
    name: null,
    created: null,
    customer_interest: null,
    owner_type: null,
    lead_source: null,
    additional_files: [],
    utility_bills: [],
    notes: null,
    opportunity_sub_status: 0,
    disposition: 0,
    sub_status: 0,
    property: {
      latitude: 0.0,
      longitude: 0.0,
      calculated_system_size: null,
      calculated_system_production: null,
      site_address: null,
      building_use: null,
      roof_complexity: null,
      utility_name: null,
      county: null,
      utility_rate_schedule: null,
      avoided_cost: null,
      pre_solar_annual_utility_cost: 0,
      annual_consumption: 0,
      install_type: null,
      energy_consumption_estimate: 0,
      rooftop_solar_area: 0,
      rooftop_solar_capacity: 0,
      has_solar: null,
      pace_eligibility: null,
      solar_cost: 0,
      estimated_epc_cost: null,
      prospect_now: null,
    },
    design_data: {
      proposed_system_size: null,
      estimated_production: null,
      epc_cost: null,
      roof_required: null,
      roofing_costs: null,
      survey_status: null,
      survey_scheduled_date: null,
      survey_complete_date: null,
      survey_approved_complete: null,
      construction_start_date: null,
      total_roof_size: null,
      helioscope_link: null,
    },
    customer: {
      emails: [],
      first_name: '',
      last_name: '',
      mailing_address: '',
      name: '',
      phone_extension: '',
      phone_numbers: [],
      role: '',
    },
    project_info: {
      avoided_cost: null,
      epc_price: null,
      estimated_year_production: null,
      install_type: null,
      lead: null,
      system_size: null,
      user_input_data: {
        avoided_cost: null,
        epc_price: null,
        estimated_year_production: null,
      },
      ess_capacity: null,
      ess_size: null,
      ess_epc_price: null,
    },
    proposals: [],
    agreements: [],
    quotes: [],
    system_image: null,
    organization: null,
    associated_user_name: null,
    associated_user: null,
    sdr_finance_status: 0,
    energy_reseller: null,
    is_proposals_locked: null,
  },
  fetchErrors: null,
  error: null,
  editErrors: null,
  activeTab: 'quotes',
  deleteSucceed: null,
  deleteErrors: null,
  documentUrl: '',
};

export default (state: State = initialState, action: ActionT = {}): State =>
  produce(state, (draft) => {
    // immer returns initial state by default
    // eslint-disable-next-line default-case
    switch (action.type) {
      case FETCH_LEAD:
        draft.isFetching = true;
        draft.fetchErrors = null;
        draft.error = null;
        return;
      case FETCH_SUCCESS:
        draft.data = action.payload;
        try {
          draft.data.customer = action.payload.customer || initialState.data.customer;
          draft.data.design_data = action.payload.design_data || initialState.data.design_data;
        } catch (e) {
          draft.data.customer = initialState.data.customer;
          draft.data.design_data = initialState.data.design_data;
        }
        draft.fetchErrors = null;
        draft.error = null;
        draft.isFetching = false;
        return;
      case FETCH_FAILED:
        draft.isFetching = false;
        draft.error = action.payload.response;
        draft.fetchErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.fetchErrors.forEach((error) => message.error(error, 10));
        return;
      case UPDATE_LEAD:
      case REMOVE_SYSTEM_IMAGE:
        draft.isFetching = true;
        return;
      case UPDATE_SUCCESS:
        draft.data = {
          ...draft.data,
          ...action.payload,
        };
        draft.data.customer = action.payload.customer || initialState.data.customer;
        draft.data.design_data = action.payload.design_data || initialState.data.design_data;
        draft.data.project_info = action.payload.project_info || initialState.data.project_info;
        draft.editFormData = initialState.editFormData;
        draft.isEditing = false;
        draft.isFetching = false;
        draft.editErrors = initialState.editErrors;
        message.success('Lead updated successfully.');
        return;
      case UPDATE_FAILED:
        draft.isFetching = false;
        draft.isEditing = true;
        draft.editErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.editErrors.forEach((error) => message.error(error, 10));
        return;
      case SET_EDIT_MODE:
        draft.isEditing = action.payload;
        return;
      case CHANGE_TAB:
        draft.activeTab = action.payload;
        return;
      case DELETE_LEAD:
        draft.isFetching = true;
        return;
      case DELETE_SUCCESS:
        draft.deleteSucceed = true;
        draft.isFetching = false;
        draft.deleteErrors = initialState.deleteErrors;
        message.success('Lead cancelled.');
        return;
      case DELETE_FAILED: {
        draft.deleteSucceed = false;
        draft.isFetching = false;
        draft.deleteErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.deleteErrors.forEach((error) => message.error(error, 10));
        return;
      }
      case leadsActionsTypes.FETCH_SUCCESS:
        draft.deleteSucceed = initialState.deleteSucceed;
        return;
      case SAVE_EDIT_DATA:
        draft.editFormData = {
          ...draft.editFormData,
          ...action.payload,
        };
        return;
      case CLEAR_EDIT_DATA:
        draft.editFormData = initialState.editFormData;
        return;
      case FETCH_PROJECT_INFORMATION_DETAILS:
        draft.isFetching = true;
        draft.fetchErrors = null;
        draft.error = null;
        return;
      case FETCH_PROJECT_INFORMATION_DETAILS_SUCCESS:
        draft.isFetching = false;
        draft.fetchErrors = null;
        draft.error = null;
        return;
      case FETCH_PROJECT_INFORMATION_DETAILS_FAILED:
        draft.isFetching = false;
        draft.fetchErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.fetchErrors.forEach((error) => message.error(error, 10));
        draft.error = action.payload.response;
        return;
      case FETCH_DOCUMENT:
        draft.isFetching = true;
        draft.fetchErrors = null;
        draft.error = null;
        return;
      case FETCH_DOCUMENT_SUCCESS:
        draft.isFetching = false;
        draft.fetchErrors = null;
        draft.error = null;
        draft.documentUrl = action.payload;
        return;
      case FETCH_DOCUMENT_FAILED:
        draft.isFetching = false;
        draft.fetchErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.fetchErrors.forEach((error) => message.error(error, 10));
        draft.error = action.payload.response;
        return;
      case RESET_DOCUMENT_URL:
        draft.isFetching = true;
        draft.fetchErrors = null;
        draft.error = null;
        return;
      case RESET_DOCUMENT_URL_SUCCESS:
        draft.isFetching = false;
        draft.fetchErrors = null;
        draft.error = null;
        draft.documentUrl = '';
        return;
      case RESET_DOCUMENT_URL_FAILED:
        draft.isFetching = false;
        draft.fetchErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.fetchErrors.forEach((error) => message.error(error, 10));
        draft.error = action.payload.response;
        return;
    }
  });
